import {
  getPublicClientFor,
  isEvmChainId,
  useLogOnce,
} from '@funkit/connect-core'
import { useQuery } from '@tanstack/react-query'
import { erc20Abi } from 'viem'

import type { WithdrawalToken } from '../flow/withdrawalFlowConfig'
import { logger } from '../logging/logger'

export const SOURCE_TOKEN_DECIMALS_QUERY_KEY = 'sourceTokenDecimals'

/**
 * Decimals for the amount conversion: the host's value when it has one,
 * otherwise read on-chain. A stale host value would mis-scale the transfer, so
 * the read is preferred over a guess.
 *
 * Its own query rather than a step inside the quote, so the read runs once per
 * token — as soon as the form knows what it is spending — instead of on every
 * 25-second quote refresh.
 */
export function useSourceTokenDecimals(
  sourceToken: WithdrawalToken,
): number | undefined {
  const canReadOnChain = isEvmChainId(sourceToken.chainId)
  const missingDecimals = sourceToken.decimals == null

  // Nothing can fill this in, so the form would never convert an amount.
  useLogOnce(
    missingDecimals && !canReadOnChain
      ? `${sourceToken.chainId}:${sourceToken.address}`
      : undefined,
    () => {
      logger.warn('connect-rn:withdrawal:missing_source_decimals', {
        chainId: sourceToken.chainId,
        address: sourceToken.address,
      })
    },
  )

  const query = useQuery({
    queryKey: [
      SOURCE_TOKEN_DECIMALS_QUERY_KEY,
      sourceToken.chainId,
      sourceToken.address,
    ],
    queryFn: async () => {
      const client = getPublicClientFor(Number(sourceToken.chainId))
      if (!client) {
        throw new Error(
          `No RPC client for chain ${sourceToken.chainId} — cannot read token decimals`,
        )
      }
      return client.readContract({
        address: sourceToken.address,
        abi: erc20Abi,
        functionName: 'decimals',
      })
    },
    enabled: missingDecimals && canReadOnChain,
    // A token's decimals never change, so one read lasts the session.
    staleTime: Number.POSITIVE_INFINITY,
    retry: 1,
  })

  return sourceToken.decimals ?? query.data
}
